<script lang="ts">
    import { canvasEditor, activeToolId } from '../../stores';
    import { Cursor as CursorIcon, NoteBlank as NoteBlankIcon, TextT as TextTIcon, ArticleMedium as ArticleMediumIcon, FlowArrow as FlowArrowIcon, SmileySticker as SmileyStickerIcon } from 'phosphor-svelte';

    type ToolDef = { id: string; label: string; shortcut: string | null; icon: typeof CursorIcon; disabled?: boolean };

    const tools: ToolDef[] = [
        { id: 'select', label: 'Select', shortcut: null, icon: CursorIcon },
        { id: 'sticky-note', label: 'Sticky Note', shortcut: 'N', icon: NoteBlankIcon },
        { id: 'header', label: 'Header', shortcut: 'H', icon: TextTIcon },
        { id: 'markdown', label: 'Markdown', shortcut: 'M', icon: ArticleMediumIcon },
        { id: 'sticker', label: 'Sticker', shortcut: null, icon: SmileyStickerIcon, disabled: true },
        { id: 'connect', label: 'Connect', shortcut: 'C', icon: FlowArrowIcon, disabled: true },
    ];

    function handleToolClick(tool: ToolDef, event: MouseEvent) {
        if (tool.disabled) return;
        const editor = $canvasEditor;
        if (!editor) return;

        if (tool.id === 'select') {
            editor.handleToolbarClick('select', false);
        } else {
            editor.handleToolbarClick(tool.id, event.shiftKey);
        }
    }
</script>

<div class="toolbar" aria-label="Canvas tools">
    {#each tools as tool (tool.id)}
        <button
            class="tool-button"
            class:active={tool.id === 'select' ? !$activeToolId : $activeToolId === tool.id}
            class:disabled={tool.disabled}
            type="button"
            disabled={tool.disabled}
            aria-label="{tool.label}{tool.shortcut ? ` (${tool.shortcut})` : ''}"
            title="{tool.label}{tool.shortcut ? ` (${tool.shortcut}, Shift+${tool.shortcut} to lock)` : ''}{tool.disabled ? ' (coming soon)' : ''}"
            on:click={(e) => handleToolClick(tool, e)}
        >
            <svelte:component this={tool.icon} size={20} />
        </button>
    {/each}
</div>

<style>
    .toolbar {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);

        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 6px;

        background: var(--panelSolid);
        border: 1px solid var(--border);
        border-radius: 14px;
        box-shadow:
            0px 5.23px 6.54px -3.92px rgba(0, 0, 0, 0.1),
            0px 13.08px 16.35px -3.27px rgba(0, 0, 0, 0.1);

        z-index: 9998;
        pointer-events: auto;
    }

    .tool-button {
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border-radius: 10px;

        background: transparent;
        border: none;
        color: var(--muted);
        cursor: pointer;

        transition:
            background 0.15s ease,
            color 0.15s ease,
            transform 0.06s ease;
    }

    .tool-button:hover {
        background: var(--hover);
        color: var(--text);
    }

    .tool-button:active {
        transform: scale(0.95);
    }

    .tool-button.active {
        background: var(--hover);
        color: var(--accent);
    }

    .tool-button.disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    .tool-button.disabled:hover {
        background: transparent;
        color: var(--muted);
    }

    .tool-button :global(svg) {
        width: 20px;
        height: 20px;
    }
</style>
